/**
 * @file Authentication service — wraps every auth-related API endpoint.
 *
 * Supports three authentication flows:
 *   1. **Patient email** — passwordless magic-code via email.
 *   2. **Patient phone** — passwordless magic-code via WhatsApp (public booking).
 *   3. **Professional** — email + password with email verification.
 *
 * Also covers Google/Apple OAuth, password reset, and session management.
 *
 * @module services/auth
 */
import api from "./api";

/**
 * Auth API methods grouped by flow.
 * @type {Object}
 */
export const authService = {
  // ── Patient email flow ────────────────────────────────────────────────
  /** @param {{ firstName: string, lastName: string, email: string }} data */
  patientRegister: (data) => api.post("/auth/patient/register", data),
  /** @param {string} email */
  requestCode: (email) => api.post("/auth/patient/request-code", { email }),
  /** @param {string} email  @param {string} code - 6-digit magic code */
  verifyCode: (email, code) => api.post("/auth/patient/verify-code", { email, code }),

  // ── Patient phone flow (public booking) ───────────────────────────────
  /** @param {{ firstName: string, lastName: string, phone: string }} data */
  patientPhoneRegister: (data) => api.post("/auth/patient/phone-register", data),
  /** @param {string} phone - E.164 format */
  requestPhoneCode: (phone) => api.post("/auth/patient/request-phone-code", { phone }),
  /** @param {string} phone  @param {string} code */
  verifyPhoneCode: (phone, code) => api.post("/auth/patient/verify-phone-code", { phone, code }),
  /** @param {string} phone - Add phone to an existing patient account */
  addPhone: (phone) => api.post("/auth/patient/add-phone", { phone }),
  /** @param {string} email - Add email to an existing patient account and send verification code */
  addEmail: (email) => api.post("/auth/patient/add-email", { email }),
  /** @param {string} code - Verify the email code sent to patient */
  verifyEmailCode: (code) => api.post("/auth/patient/verify-email-code", { code }),

  // ── Professional flow ─────────────────────────────────────────────────
  /** @param {{ firstName: string, lastName: string, email: string, password: string }} data */
  professionalRegister: (data) => api.post("/auth/professional/register", data),
  /** @param {string} token - Email verification JWT */
  verifyEmail: (token) => api.post("/auth/professional/verify-email", { token }),
  /** @param {string} email  @param {string} password */
  login: (email, password) => api.post("/auth/login", { email, password }),
  /** @param {string} email - Resend verification email */
  resendVerificationEmail: (email) => api.post("/auth/professional/resend-verification-email", { email }),
  /** @param {string} phone */
  professionalAddPhone: (phone) => api.post("/auth/professional/add-phone", { phone }),
  /** @param {string} phone  @param {string} code */
  professionalVerifyPhone: (phone, code) => api.post("/auth/professional/verify-phone", { phone, code }),

  // ── Password reset ────────────────────────────────────────────────────
  /** @param {string} email */
  forgotPassword: (email) => api.post("/auth/forgot-password", { email }),
  /** @param {string} token  @param {string} password */
  resetPassword: (token, password) => api.post("/auth/reset-password", { token, password }),
  /** @param {string} currentPassword  @param {string} newPassword */
  changePassword: (currentPassword, newPassword) => api.post("/auth/change-password", { currentPassword, newPassword }),

  // ── OAuth (Google + Apple) ───────────────────────────────────────────
  /** @param {string} token - Google ID token  @param {string} role  @param {string} [referralCode]  @param {boolean} [acceptTerms] */
  googleLogin: (token, role, referralCode, acceptTerms) =>
    api.post("/auth/google", { token, role, referralCode: referralCode || undefined, acceptTerms: acceptTerms || undefined }),
  /** @param {string} token  @param {Object} extras - Additional registration fields (title, phone, acceptTerms) */
  googleRegister: (token, extras) => api.post("/auth/google/register", { token, ...extras }),
  /** @param {string} token - Apple identity token  @param {string} role  @param {string} firstName  @param {string} lastName  @param {string} [referralCode]  @param {boolean} [acceptTerms] */
  appleLogin: (token, role, firstName, lastName, referralCode, acceptTerms) =>
    api.post("/auth/apple", { token, role, firstName, lastName, referralCode: referralCode || undefined, acceptTerms: acceptTerms || undefined }),
  /** @param {string} token  @param {Object} extras - Additional registration fields (title, phone, firstName, lastName, acceptTerms) */
  appleRegister: (token, extras) => api.post("/auth/apple/register", { token, ...extras }),

  // ── Shared ────────────────────────────────────────────────────────────
  /** Refresh access token (refresh token is in httpOnly cookie) */
  refreshToken: () => api.post("/auth/refresh"),
  logout: () => api.post("/auth/logout"),
  /** @returns {Promise<{ data: { user: Object } }>} Current user info */
  getMe: () => api.get("/auth/me"),
  /** Accept Terms of Service and Privacy Policy — returns fresh JWT */
  acceptTerms: () => api.post("/auth/accept-terms"),
};
